"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CampusEvent, EventFilters } from "@/app/types/events";
import type { EventsDirectoryViewProps } from "./EventsDirectoryView";
import {
  DEFAULT_EVENT_FILTERS,
  INTRAMURAL_EVENT_SOURCES,
  DEFAULT_SPORTS_EVENT_SOURCES,
  type EventTab,
  type SportItem,
} from "./eventsDirectoryViewModel";

export const EVENTS_PAGE_SIZE = 30;

type EventsResponse = {
  events?: CampusEvent[];
  total?: number | null;
  error?: unknown;
};

export function buildEventsQuery(filters: EventFilters, page: number): string {
  const params = new URLSearchParams();
  filters.sources.forEach((source) => params.append("source", source));
  if (filters.category) params.set("category", filters.category);
  if (filters.search) params.set("q", filters.search);
  if (filters.dateRange !== "all") {
    params.set("date_range", filters.dateRange);
  }
  params.set("limit", String(EVENTS_PAGE_SIZE));
  params.set("offset", String(page * EVENTS_PAGE_SIZE));
  return params.toString();
}

export type EventsDirectoryController = Omit<
  EventsDirectoryViewProps,
  "observerTarget"
> & {
  observerTarget: React.RefObject<HTMLDivElement | null>;
};

/**
 * Owns the live Events directory state without allowing an older filter/page
 * request to overwrite the current view. The observer lock is synchronous so
 * one intersection delivery cannot enqueue several pages before React paints
 * the loading state.
 */
export function useEventsDirectoryController(): EventsDirectoryController {
  const [events, setEvents] = useState<CampusEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<EventTab>("clubs");
  const [activeSport, setActiveSport] = useState<string | null>(null);
  const [filters, setFilters] = useState<EventFilters>(DEFAULT_EVENT_FILTERS);
  const [retryRevision, setRetryRevision] = useState(0);
  const observerTarget = useRef<HTMLDivElement>(null);
  const requestGeneration = useRef(0);
  const paginationLocked = useRef(true);

  const query = useMemo(() => buildEventsQuery(filters, page), [filters, page]);

  const updateFilters = useCallback<EventsDirectoryViewProps["onSetFilters"]>(
    (next) => {
      paginationLocked.current = true;
      setPage(0);
      setFilters(next);
    },
    [],
  );

  const selectTab = useCallback(
    (tab: EventTab) => {
      setActiveTab(tab);
      setActiveSport(null);
      if (tab === "clubs") {
        updateFilters(DEFAULT_EVENT_FILTERS);
      } else if (tab === "rutgers") {
        updateFilters((current) => ({
          ...current,
          sources: DEFAULT_SPORTS_EVENT_SOURCES,
          search: "",
          category: null,
        }));
      } else if (tab === "intramural") {
        updateFilters((current) => ({
          ...current,
          sources: INTRAMURAL_EVENT_SOURCES,
          search: "",
          category: null,
        }));
      } else {
        updateFilters((current) => ({
          ...current,
          sources: [],
          search: "",
          category: "Study Space",
        }));
      }
    },
    [updateFilters],
  );

  const selectSport = useCallback(
    (tab: EventTab, sport: SportItem) => {
      const defaultSources =
        tab === "rutgers" ? DEFAULT_SPORTS_EVENT_SOURCES : INTRAMURAL_EVENT_SOURCES;
      const preserveSources = activeTab === tab;
      if (activeTab === tab && activeSport === sport.query) {
        setActiveSport(null);
        updateFilters((current) => ({ ...current, sources: preserveSources ? current.sources : defaultSources, search: "" }));
        return;
      }
      setActiveTab(tab);
      setActiveSport(sport.query);
      updateFilters((current) => ({
        ...current,
        sources: preserveSources ? current.sources : defaultSources,
        search: sport.query,
        category: null,
      }));
    },
    [activeSport, activeTab, updateFilters],
  );

  useEffect(() => {
    const controller = new AbortController();
    const generation = ++requestGeneration.current;
    const reset = page === 0;

    paginationLocked.current = true;
    setLoading(true);
    setError(null);
    if (reset) {
      setEvents([]);
      setTotalCount(null);
      setHasMore(true);
    }

    void (async () => {
      try {
        const response = await fetch(`/api/events?${query}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = (await response.json()) as EventsResponse;
        if (data.error) throw new Error("Upstream events service failed");
        const fetched = Array.isArray(data.events) ? data.events : [];
        if (generation !== requestGeneration.current) return;

        setEvents((previous) => (reset ? fetched : [...previous, ...fetched]));
        setTotalCount(
          typeof data.total === "number" && Number.isFinite(data.total)
            ? data.total
            : null,
        );
        setHasMore(fetched.length === EVENTS_PAGE_SIZE);
      } catch {
        if (
          controller.signal.aborted ||
          generation !== requestGeneration.current
        ) {
          return;
        }
        setError("Failed to load events. Check your connection or try again.");
        setHasMore(false);
      } finally {
        if (generation === requestGeneration.current) {
          paginationLocked.current = false;
          setLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [page, query, retryRevision]);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          !entries[0]?.isIntersecting ||
          !hasMore ||
          loading ||
          paginationLocked.current
        ) {
          return;
        }
        paginationLocked.current = true;
        setPage((current) => current + 1);
      },
      { threshold: 0.1, rootMargin: "200px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  const retry = useCallback(() => {
    paginationLocked.current = true;
    setPage(0);
    setRetryRevision((current) => current + 1);
  }, []);

  const changeSearch = useCallback(
    (search: string) => {
      setActiveSport(null);
      updateFilters((current) => ({ ...current, search }));
    },
    [updateFilters],
  );

  return {
    events,
    loading,
    error,
    hasMore,
    totalCount,
    activeTab,
    activeSport,
    filters,
    showMobileFilters,
    observerTarget,
    onSelectTab: selectTab,
    onSelectSport: selectSport,
    onSetFilters: updateFilters,
    onSearchChange: changeSearch,
    onOpenFilters: () => setShowMobileFilters(true),
    onCloseFilters: () => setShowMobileFilters(false),
    onRetry: retry,
  };
}
